import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Loader2, MessageSquare, Send, AlertTriangle, Trash2, Edit3 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Renders interactive (editable) or read-only stars. */
function StarRating({ value, onChange, size = 5, readOnly = false }) {
  const sizeClass = size === 5 ? 'w-5 h-5' : size === 6 ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => !readOnly && onChange?.(n)}
            disabled={readOnly}
            className={`p-0.5 transition-transform ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
            data-testid={`star-${n}`}
          >
            <Star className={`${sizeClass} ${filled ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
          </button>
        );
      })}
    </div>
  );
}

function formatDate(d, locale) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

export default function ShopReviewsSection({ shopId, shopOwnerId }) {
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-GB' : 'it-IT';

  const [summary, setSummary] = useState({ average: 0, count: 0, distribution: {} });
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [rating, setRating] = useState(0);
  const [commento, setCommento] = useState('');

  // Owner reply state per review
  const [replyOpen, setReplyOpen] = useState({});
  const [replyText, setReplyText] = useState({});

  const isOwner = isAuthenticated && user?.user_id && shopOwnerId === user.user_id;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shops/${shopId}/reviews`);
      setSummary(res.data.summary);
      setReviews(res.data.items || []);
      if (isAuthenticated) {
        try {
          const mineRes = await axios.get(`${API}/shops/${shopId}/reviews/mine`, { withCredentials: true });
          if (mineRes.data) {
            setMyReview(mineRes.data);
            setRating(mineRes.data.stelle || 0);
            setCommento(mineRes.data.commento || '');
          }
        } catch (e) {
          console.debug('No existing review:', e?.message);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [shopId, isAuthenticated]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error(t('reviews.err_no_stars'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/shops/${shopId}/reviews`,
        { stelle: rating, commento: commento.trim() },
        { withCredentials: true },
      );
      setMyReview(res.data);
      setShowForm(false);
      toast.success(myReview ? t('reviews.updated') : t('reviews.thanks'));
      await fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('reviews.err_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMine = async () => {
    if (!window.confirm(t('reviews.confirm_delete'))) return;
    try {
      await axios.delete(`${API}/shops/${shopId}/reviews/mine`, { withCredentials: true });
      setMyReview(null);
      setRating(0);
      setCommento('');
      toast.success(t('reviews.deleted'));
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(t('reviews.err_generic'));
    }
  };

  const handleReply = async (reviewId) => {
    const text = (replyText[reviewId] || '').trim();
    if (!text) return;
    try {
      await axios.post(
        `${API}/shops/my/reviews/${reviewId}/response`,
        { response: text },
        { withCredentials: true },
      );
      toast.success(t('reviews.reply_sent'));
      setReplyOpen((p) => ({ ...p, [reviewId]: false }));
      setReplyText((p) => ({ ...p, [reviewId]: '' }));
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(t('reviews.err_generic'));
    }
  };

  const handleFlag = async (reviewId) => {
    if (!window.confirm(t('reviews.confirm_flag'))) return;
    try {
      await axios.post(
        `${API}/shops/my/reviews/${reviewId}/flag`,
        {},
        { withCredentials: true },
      );
      toast.success(t('reviews.flagged'));
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(t('reviews.err_generic'));
    }
  };

  return (
    <section className="bg-white rounded-2xl p-5 shadow-sm" data-testid="shop-reviews-section">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-outfit font-semibold text-lg text-slate-800">{t('reviews.title')}</h2>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-bold text-slate-800" data-testid="reviews-average">
              {summary.count > 0 ? summary.average.toFixed(1) : '—'}
            </span>
            <StarRating value={Math.round(summary.average)} readOnly size={5} />
            <span className="text-sm text-slate-500" data-testid="reviews-count">
              ({summary.count} {summary.count === 1 ? t('reviews.review_one') : t('reviews.review_other')})
            </span>
          </div>
        </div>
        {isAuthenticated && !isOwner && !showForm && !myReview && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            data-testid="leave-review-button"
          >
            <Star className="w-4 h-4 mr-1" />
            {t('reviews.leave_review')}
          </Button>
        )}
      </div>

      {/* Distribution bars */}
      {summary.count > 0 && (
        <div className="space-y-1 mb-4" data-testid="rating-distribution">
          {[5, 4, 3, 2, 1].map((n) => {
            const count = summary.distribution?.[String(n)] || 0;
            const pct = summary.count ? (count / summary.count) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-slate-600">{n}</span>
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-slate-500 tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* My review (edit/show) */}
      {!isOwner && myReview && !showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4" data-testid="my-review-card">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-blue-700 uppercase">{t('reviews.your_review')}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setShowForm(true)}
                className="text-blue-600 hover:bg-blue-100 p-1 rounded"
                data-testid="edit-my-review"
                aria-label={t('common.save')}
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleDeleteMine}
                className="text-red-600 hover:bg-red-100 p-1 rounded"
                data-testid="delete-my-review"
                aria-label={t('common.cancel')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <StarRating value={myReview.stelle} readOnly size={4} />
          {myReview.commento && <p className="text-sm text-slate-700 mt-2">{myReview.commento}</p>}
        </div>
      )}

      {/* Compose form */}
      {showForm && isAuthenticated && !isOwner && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="review-form">
          <Label className="text-sm font-semibold text-slate-700">{t('reviews.your_rating')}</Label>
          <div className="mt-1">
            <StarRating value={rating} onChange={setRating} size={6} />
          </div>
          <Label className="text-sm font-semibold text-slate-700 mt-3 block">{t('reviews.your_comment')}</Label>
          <Textarea
            value={commento}
            onChange={(e) => setCommento(e.target.value.slice(0, 600))}
            placeholder={t('reviews.comment_placeholder')}
            rows={3}
            className="mt-1 rounded-xl resize-none"
            data-testid="review-comment-input"
          />
          <p className="text-xs text-slate-500 mt-1 text-right">{commento.length}/600</p>
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowForm(false);
                if (myReview) {
                  setRating(myReview.stelle);
                  setCommento(myReview.commento || '');
                } else {
                  setRating(0);
                  setCommento('');
                }
              }}
              disabled={submitting}
              data-testid="cancel-review-button"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || rating < 1}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
              data-testid="submit-review-button"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <Send className="w-4 h-4 mr-1" />
              {myReview ? t('reviews.update') : t('reviews.publish')}
            </Button>
          </div>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="py-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-center text-slate-500 py-6 text-sm" data-testid="no-reviews-msg">
          {isAuthenticated && !isOwner ? t('reviews.empty_be_first') : t('reviews.empty')}
        </p>
      ) : (
        <ul className="space-y-3 divide-y divide-slate-100">
          {reviews.map((r) => (
            <li key={r.id} className="pt-3 first:pt-0" data-testid={`review-${r.id}`}>
              <div className="flex items-start gap-3">
                {r.user_picture ? (
                  <img src={r.user_picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-700 font-semibold text-sm">
                      {(r.user_name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-800">{r.user_name}</span>
                    <StarRating value={r.stelle} readOnly size={4} />
                    <span className="text-xs text-slate-500 ml-auto">
                      {formatDate(r.created_at, locale)}
                    </span>
                  </div>
                  {r.commento && (
                    <p className="text-sm text-slate-700 mt-1 break-words">{r.commento}</p>
                  )}

                  {/* Owner reply */}
                  {r.owner_response && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-2.5 mt-2 text-sm">
                      <p className="text-xs font-semibold text-blue-700 uppercase mb-1">
                        {t('reviews.owner_response')}
                      </p>
                      <p className="text-slate-700">{r.owner_response}</p>
                    </div>
                  )}

                  {/* Owner controls */}
                  {isOwner && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {!r.owner_response && (
                        <button
                          onClick={() => setReplyOpen((p) => ({ ...p, [r.id]: !p[r.id] }))}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          data-testid={`reply-to-${r.id}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {t('reviews.reply')}
                        </button>
                      )}
                      {!r.flagged && (
                        <button
                          onClick={() => handleFlag(r.id)}
                          className="text-xs text-amber-600 hover:underline flex items-center gap-1 ml-2"
                          data-testid={`flag-${r.id}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {t('reviews.flag')}
                        </button>
                      )}
                      {r.flagged && (
                        <span className="text-xs text-amber-600 flex items-center gap-1 ml-2">
                          <AlertTriangle className="w-3 h-3" />
                          {t('reviews.flagged_label')}
                        </span>
                      )}
                    </div>
                  )}

                  {replyOpen[r.id] && isOwner && (
                    <div className="mt-2 flex gap-2">
                      <Textarea
                        value={replyText[r.id] || ''}
                        onChange={(e) => setReplyText((p) => ({ ...p, [r.id]: e.target.value.slice(0, 600) }))}
                        placeholder={t('reviews.reply_placeholder')}
                        rows={2}
                        className="rounded-xl resize-none text-sm"
                        data-testid={`reply-input-${r.id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleReply(r.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`reply-submit-${r.id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!isAuthenticated && (
        <p className="text-center text-xs text-slate-500 mt-3">
          {t('reviews.login_to_review')}
        </p>
      )}
    </section>
  );
}
